import { useEffect, useRef } from 'react';

const PRELOAD_MAX_AGE = 2000;
const TIME_TO_INTENT = 200;

export type UsePressablePreloadRefParams<T> = {
	load: () => T;
	onBeforeLoad: () => void;
	onLoad: (obj: { reference: T; dispose: () => void }) => void;
};

export function usePressablePreloadRef<T extends { dispose: () => void }>({
	load,
	onBeforeLoad,
	onLoad,
}: UsePressablePreloadRefParams<T>) {
	const ref = useRef<HTMLElement>(null);

	useEffect(() => {
		const el = ref.current;
		if (!el) {
			return;
		}

		const request: {
			load?: { reference: T; dispose: () => void };
			preload?: { reference: T; dispose: () => void };
			preloadIntentTimeout?: NodeJS.Timeout;
			preloadMaxAgeTimeout?: NodeJS.Timeout;
		} = {};

		const clearPreloadTimeouts = () => {
			if (typeof request.preloadIntentTimeout === 'number') {
				clearTimeout(request.preloadIntentTimeout);
				delete request.preloadIntentTimeout;
			}

			if (typeof request.preloadMaxAgeTimeout === 'number') {
				clearTimeout(request.preloadMaxAgeTimeout);
				delete request.preloadMaxAgeTimeout;
			}
		};

		const cancelPreload = () => {
			clearPreloadTimeouts();
			request.preload?.dispose();
		};

		const loadReference = () => {
			if (request.load) {
				return request.load;
			}

			const reference = request.preload?.reference ?? load();
			if (request.preload) {
				delete request.preload;
			}

			request.load = {
				reference,
				dispose() {
					reference.dispose();
					delete request.load;
				},
			};

			return request.load;
		};

		const clickListener = () => {
			clearPreloadTimeouts();
			onBeforeLoad();
			onLoad(loadReference());
		};

		const mouseDownListener = () => {
			clearPreloadTimeouts();
			loadReference();
		};

		const preloadReference = () => {
			if (request.preloadIntentTimeout || request.preload) {
				return;
			}

			request.preloadIntentTimeout = setTimeout(() => {
				delete request.preloadIntentTimeout;

				const reference = load();

				request.preload = {
					reference,
					dispose() {
						reference.dispose();
						delete request.preload;
					},
				};

				request.preloadMaxAgeTimeout = setTimeout(() => {
					delete request.preloadMaxAgeTimeout;
					request.preload?.dispose();
				}, PRELOAD_MAX_AGE);
			}, TIME_TO_INTENT);
		};

		el.addEventListener('click', clickListener);
		el.addEventListener('mousedown', mouseDownListener);

		el.addEventListener('mouseenter', preloadReference);
		el.addEventListener('mouseleave', cancelPreload);

		return () => {
			cancelPreload();

			el.removeEventListener('click', clickListener);
			el.removeEventListener('mousedown', mouseDownListener);

			el.removeEventListener('mouseenter', preloadReference);
			el.removeEventListener('mouseleave', cancelPreload);
		};
	}, [load, onBeforeLoad, onLoad]);

	return ref;
}
